import type { useQuery } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInputMenu from "@repo/components/src/forms/menu";
import { Box } from "@repo/components/src/ui/box";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import {
  Progress,
  ProgressFilledTrack,
} from "@repo/components/src/ui/progress";
import { SkeletonText } from "@repo/components/src/ui/skeleton";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  getCountryName,
  getGenderName,
} from "@repo/components/src/utils/countries";
import { formatPercentage } from "@repo/components/src/utils/numbers";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { AudienceSchema, audienceSchema } from "./schema";
import { AccountMetricTotalValueQuery, AccountMetricTotalValueQueryVariables } from "@repo/graphql/documents/accountMetric/query.generated";

type Props = {
  accountMetricQuery: useQuery.Result<
    AccountMetricTotalValueQuery,
    AccountMetricTotalValueQueryVariables
  >;
};

const OPTIONS = [
  { label: "País", value: "audience_countries", dimensionKey: "country" },
  { label: "Ciudad", value: "audience_cities", dimensionKey: "city_name" },
  { label: "Género", value: "audience_genders", dimensionKey: "gender" },
  { label: "Edad", value: "audience_ages", dimensionKey: "age" },
];

const getCityName = (city: string) => {
  const splitted = city.split(" ");
  const country = splitted[0];
  const cityName = splitted.slice(1).join(" ");
  return `${getCountryName(country)}, ${cityName}`;
};

const AudienceSummary = React.memo((props: Props) => {
  const { accountMetricQuery } = props;
  const { data, loading } = accountMetricQuery;
  const formProps = useForm<AudienceSchema>({
    resolver: yupResolver(audienceSchema),
    defaultValues: {
      topic: "audience_genders",
    },
  });
  const selectedType = useWatch({
    control: formProps.control,
    name: "topic",
  });
  const selectedOption = OPTIONS.find(
    (option) => option.value === selectedType,
  );
  const audienceMetric = data?.accountMetric.totalValues.find((totalValue) => {
      const accountMetric = totalValue.accountMetric;
      return accountMetric.name === selectedType;
    });
  const breakdownResults =
    audienceMetric?.breakdowns
      ?.find((b) =>
        b.dimensionKeys.includes(selectedOption?.dimensionKey ?? ""),
      )
      ?.results.slice()
      .sort((a, b) => b.value - a.value)
      .slice(0, 5) ?? [];
  return (
    <>
      <HStack space="lg">
        <Heading className="font-medium flex-1 shrink" numberOfLines={1}>
          Audiencia
        </Heading>
        <Box className="">
          <FormInputMenu
            control={formProps.control}
            name="topic"
            options={OPTIONS}
            size="sm"
            labelSize="md"
            headingProps={{ bold: false }}
            buttonTriggerProps={{
              rounded: "lg",
              action: "brand",
              size: "xs",
            }}
          />
        </Box>
      </HStack>
      <Box className="mt-4">
        <SkeletonText
          className="h-4 rounded-lg"
          isLoaded={!loading}
          _lines={5}
          gap={4}
        >
          <VStack space="md">
            {breakdownResults.map((result) => {
              return (
                <Box key={result.dimensionValues[0]}>
                  <HStack space="lg">
                    <Text size="md" className="flex-1">
                      {selectedType === "audience_countries"
                        ? getCountryName(result.dimensionValues[0])
                        : selectedType === "audience_genders"
                          ? getGenderName(result.dimensionValues[0])
                          : selectedType === "audience_cities"
                            ? getCityName(result.dimensionValues[0])
                            : result.dimensionValues[0]}
                    </Text>
                    <Text className="text-right">
                      {formatPercentage(result.value, 0)}
                    </Text>
                  </HStack>
                  <Progress
                    action="brand"
                    size="sm"
                    value={result.value}
                    max={1}
                  >
                    <ProgressFilledTrack />
                  </Progress>
                </Box>
              );
            })}
          </VStack>
        </SkeletonText>
      </Box>
    </>
  );
});

AudienceSummary.displayName = "AudienceSummary";

export default AudienceSummary;
